import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { Box, Button, ButtonGroup, Chip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import AddIcon from "@mui/icons-material/Add";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { APIError, AppId } from "../../../../app/appTypes";
import { If } from "../../../../app/If";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { selectSettings } from "../../../settings/settingsSlice";
import { RolesLabel } from "../../Labels/ProjectRolesLabel";
import { loadProjectTeamMembers, ProjectTeamMember } from "../../projectTeamMembersSlice";
import { ValidationProjectTeamMemberDialog, ValidationProjectTeamMemberDialogProps } from "../dialogs/ValidationProjectTeamMemberDialog";
import { ValidationTeamMemberDeleteDialog, ValidationTeamMemberDeleteDialogProps } from "../dialogs/ValidationTeamMemberDeleteDialog";
import { ValidationInviteProjectTeamMemberDialog, ValidationInviteProjectTeamMemberDialogProps } from "../dialogs/ValidationInviteProjectTeamMemberDialog";
import ShareIcon from '@mui/icons-material/Share';

type DialogState
    = { tag: "none" }
    | { tag: "confirmDelete", props: ValidationTeamMemberDeleteDialogProps }
    | { tag: "addTeamMember", props: ValidationProjectTeamMemberDialogProps }
    | { tag: 'inviteTeamMember', props: ValidationInviteProjectTeamMemberDialogProps }

interface ValidationProjectTeamProps {
    projectId: AppId;
}

export const ValidationProjectTeamTab = ({ projectId }: ValidationProjectTeamProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);

    const [allTeamMembers, setAllTeamMembers] = useState<ProjectTeamMember[]>([]);
    const [loadingError, setLoadingError] = useState<APIError | null>(null);
    const teamMembersSelection = useMemo(() => new Set(allTeamMembers.map((user) => user.user)), [allTeamMembers]);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" });

    useEffect(() => {
        dispatch(loadProjectTeamMembers(projectId))
            .then(unwrapResult)
            .then((teamMembers) => {
                setAllTeamMembers(teamMembers);
                setLoadingError(null);
            })
            .catch((error) => setLoadingError(error));
    }, [dispatch, projectId]);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        }

        return defaultEnLocaleText;
    };

    const handleAddTeamMemberClick = useCallback(() => {
        setDialog({
            tag: "addTeamMember",
            props: {
                projectId,
                excludedUsers: teamMembersSelection,
                onSave: (teamMember: ProjectTeamMember) => {
                    setAllTeamMembers((current) => {
                        const index = current.findIndex((user) => user.id === teamMember.id);
                        if (index >= 0) {
                            const next = [...current];
                            next[index] = teamMember;
                            return next;
                        }

                        return [...current, teamMember];
                    });
                    setDialog({ tag: "none" });
                },
                onCancel: () => setDialog({ tag: "none" }),
            },
        });
    }, [projectId, teamMembersSelection]);

    const handleInviteTeamMemberClick = useCallback(() => {
        if (projectId) {
            setDialog({
                tag: 'inviteTeamMember',
                props: {
                    projectId: projectId,
                    onSave: (teamMember: ProjectTeamMember) => {
                        setAllTeamMembers((current) => {
                            const index = current.findIndex((user) => user.id === teamMember.id);
                            if (index >= 0) {
                                const next = [...current];
                                next[index] = teamMember;
                                return next;
                            }

                            return [...current, teamMember];
                        });
                        setDialog({ tag: "none" });
                    },
                    onCancel: () => setDialog({ tag: 'none' })
                }
            })
        }
    }, [projectId])


    const handleTeamMemberDeleteClick = useCallback((id: AppId) => {
        setDialog({
            tag: "confirmDelete",
            props: {
                teamMemberId: id,
                onSave: (teamMemberId) => {
                    setAllTeamMembers((current) => current.filter((user) => user.id !== teamMemberId));
                    setDialog({ tag: "none" });
                },
                onCancel: () => setDialog({ tag: "none" }),
            },
        });
    }, []);

    const teamcolumns: GridColDef[] = [
        {
            field: "firstName",
            headerName: l10n.getString("user-first-name", null, "Imię"),
            width: 300
        },
        {
            field: "lastName",
            headerName: l10n.getString("user-last-name", null, "Nazwisko"),
            flex: 1,
        },
        {
            field: "position",
            headerName: l10n.getString("user-position", null, "Stanowisko"),
            width: 200,
        },
        {
            field: "companyName",
            headerName: l10n.getString("user-company", null, "Firma"),
            flex: 1,
        },
        {
            field: "role",
            headerName: l10n.getString("project-role", null, "Rola w Projekcie"),
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <RolesLabel role={params.value ?? ""} />
                    {Boolean(params.row.isExternal) && <Chip
                        icon={<PublicOutlinedIcon />}
                        label={l10n.getString("project-external", null, "Zewnętrzny")}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{
                            height: 20,
                            '& .MuiChip-label': {
                                px: 0.75,
                                fontSize: "0.68rem",
                            },
                            '& .MuiChip-icon': {
                                fontSize: 13,
                                ml: 0.625,
                                mr: -0.25,
                            },
                        }}
                    />}
                </Box>
            ),
        },
        {
            field: "actions",
            type: "actions",
            width: 200,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        <If condition={params.row.role != "M"}>
                            <Button onClick={() => handleTeamMemberDeleteClick(params.row.id)} color="secondary" variant="outlined">
                                <Localized id="delete">
                                    <span>delete</span>
                                </Localized>
                            </Button>
                        </If>
                    </ButtonGroup>
                );
            },
        },
    ];

    return (
        <>
            <If condition={loadingError !== null}>
                <div>{l10n.getString("server-error", null, "Server error")}</div>
            </If>
            <DataGrid
                style={{ minHeight: 100, height: "calc(100vh - 450px)" }}
                localeText={getCustomLocaleText()}
                rows={allTeamMembers}
                columns={teamcolumns}
            />
            <If condition={true}>
                <Button sx={{ marginTop: "5px" }} variant="contained"
                    color="secondary" size="small"
                    onClick={handleAddTeamMemberClick} startIcon={<AddIcon />}>
                    <Localized id="add">
                        <span>Dodaj</span>
                    </Localized>
                </Button>
                <Button sx={{ marginLeft: "5px", marginTop: "6px" }}
                    onClick={handleInviteTeamMemberClick}
                    size="small"
                    variant="outlined" startIcon={<ShareIcon />}  >
                    <Localized id="invite">
                        <span>Zaproś</span>
                    </Localized>
                </Button>
            </If>

            <ValidationProjectTeamDialogDispatcher dialog={dialog} />
        </>
    );
};

const ValidationProjectTeamDialogDispatcher = ({ dialog }: { dialog: DialogState }) => {
    switch (dialog.tag) {
        case "none":
            return null;

        case "confirmDelete":
            return <ValidationTeamMemberDeleteDialog {...dialog.props} />;

        case "addTeamMember":
            return <ValidationProjectTeamMemberDialog {...dialog.props} />;

        case 'inviteTeamMember':
            return <ValidationInviteProjectTeamMemberDialog {...dialog.props} />
    }
};